import { AfterViewInit, Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from "@angular/core";
import { ControlContainer, FormControl, FormGroup } from "@angular/forms";
import { Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CustomValidatorMessage, evaluate, LgdErrorStateMatcher, LgdFormControl, LgdFormControlTextConfig, } from "../lgdCommon";
@Component({
    selector: "lgd-textarea",
    templateUrl: "./lgdTextArea.tmpl.html",
    styleUrls: ["./lgdTextArea.component.scss"],
    standalone: false
})
export class LgdTextAreaComponent implements OnInit, AfterViewInit, OnDestroy
{
    private readonly destructor$ = new Subject<void>();

    @ViewChild("input")
    readonly inputElement: ElementRef<HTMLInputElement>;

    private _initialized: boolean;

    @Input() readonly controlName: string | number;
    @Input() readonly label: string;
    @Input() readonly hideLabel: boolean;
    @Input() readonly tooltip: string;
    @Input() readonly rows: number = 2;

    readonly errorStateMatcher = new LgdErrorStateMatcher();

    constructor(private readonly controlContainer: ControlContainer) { }

    get initialized(): boolean
    {
        return this._initialized;
    }

    get formGroup(): FormGroup
    {
        return this.controlContainer.control as FormGroup;
    }

    get formControl(): FormControl<string>
    {
        if (!this.formGroup || (this.controlName !== undefined && this.controlName == null))
        {
            throw new Error("Cannot access either formGroup or controlName.");
        }

        const controlNameStr = typeof this.controlName === "string" ? this.controlName : `${this.controlName}`;
        return this.formGroup.get(controlNameStr) as FormControl<string>;
    }

    get lgdFormControl(): LgdFormControl<LgdFormControlTextConfig>
    {
        return LgdFormControl.isLgdFormControl(this.formControl) ? this.formControl : undefined;
    }

    get minLength(): number
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.minLength) : 0;
    }

    get showMaxLengthHint(): boolean
    {
        return !!this.lgdFormControl && evaluate(this.lgdFormControl.data.showMaxLengthHint);
    }

    get maxLength(): number
    {
        return this.lgdFormControl ? evaluate(this.lgdFormControl.data.maxLength) : 0;
    }

    get hasMaxLength(): boolean
    {
        return !!this.maxLength && this.maxLength > 0;
    }

    get length(): number
    {
        return this.formControl.value ? this.formControl.value.length : 0;
    }

    hasCustomValidators: boolean;

    customErrors: ReadonlyArray<CustomValidatorMessage>;

    ngOnInit(): void
    {
        this._initialized = true;
        if (this.lgdFormControl)
        {
            this.hasCustomValidators = !!this.lgdFormControl.data.customValidators;
            this.customErrors = this.hasCustomValidators
                ? this.lgdFormControl.data.customValidators.reduce(
                    (l, x) => l.concat(x.getErrorMessage(this.label)),
                    []
                )
                : [];
        }
    }

    ngAfterViewInit(): void
    {
        if (!this.inputElement)
        {
            console.warn(`LgdTextAreaComponent.ngAfterViewInit - ViewChild inputElement is not defined!`);
        }
        if (this.lgdFormControl)
        {
            this.lgdFormControl.onFocus.pipe(takeUntil(this.destructor$))
                .subscribe((shouldFocus: boolean) =>
                {
                    if (shouldFocus)
                    {
                        /**
                         * Calling in a setTimeout to avoid a bug in our current @angular version.
                         */
                        setTimeout(() =>
                        {
                            this.inputElement.nativeElement.focus();
                        });
                    }
                });
        }
    }

    ngOnDestroy(): void
    {
        this.destructor$.next();
        this.destructor$.complete();
    }

    hasError(id: string): boolean
    {
        return !!this.formControl && this.formControl.hasError(id);
    }

    get isInvalid(): boolean
    {
        return this.formControl.touched && this.formControl.invalid;
    }

    get isRequired(): boolean
    {
        return !!this.lgdFormControl && evaluate(this.lgdFormControl.data.required);
    }
}
